import { useRef, useCallback, useEffect } from 'react';
import { getTestRunStatus } from '@/services/evaluation/get-test-results';

const DEFAULT_MAX_POLLING_ATTEMPTS = 120;
const DEFAULT_INITIAL_POLLING_INTERVAL_MS = 2000; // 2 seconds initially
const DEFAULT_MAX_POLLING_INTERVAL_MS = 10000; // 10 seconds max

interface pollingOptions {
  maxPollingAttempts?: number;
  initialPollingInterval?: number;
  maxPollingInterval?: number;
}

interface PollTestRunStatusCallbacks {
  onSuccess: () => void;
  onError: (message: string) => void;
}

export const useTestRunStatusPolling = (options: pollingOptions = {}) => {
  const {
    maxPollingAttempts = DEFAULT_MAX_POLLING_ATTEMPTS,
    initialPollingInterval = DEFAULT_INITIAL_POLLING_INTERVAL_MS,
    maxPollingInterval = DEFAULT_MAX_POLLING_INTERVAL_MS,
  } = options;

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<number>(0);

  // Calculate progressive polling interval based on attempt count
  const calculatePollingInterval = useCallback(
    (attemptCount: number): number => {
      // every 3 attempts, increase interval by 2 seconds
      const stepSize = Math.floor(attemptCount / 3);
      const calculatedInterval = initialPollingInterval + stepSize * 2000;
      return Math.min(calculatedInterval, maxPollingInterval);
    },
    [initialPollingInterval, maxPollingInterval],
  );

  // Cleanup polling function
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingAttemptsRef.current = 0;
  }, []);

  // Poll for status of test run at interval, once status is completed, execute callback
  const pollTestRunStatus = useCallback(
    async (
      testId: string,
      runId: string,
      callbacks: PollTestRunStatusCallbacks,
    ) => {
      const { onSuccess, onError } = callbacks;

      try {
        const statusResponse = await getTestRunStatus(testId, runId);

        // if test run is completed, execute onSuccess callback
        if (statusResponse.status === 'completed') {
          clearPolling();
          onSuccess();
          return;
        }

        // else, continue polling
        pollingAttemptsRef.current += 1;

        if (pollingAttemptsRef.current >= maxPollingAttempts) {
          onError(
            'Test is taking longer than expected. Please check the results page.',
          );
          clearPolling();
          return;
        }

        // Schedule next status check
        const nextInterval = calculatePollingInterval(
          pollingAttemptsRef.current,
        );
        pollingIntervalRef.current = setTimeout(() => {
          pollTestRunStatus(testId, runId, callbacks);
        }, nextInterval);
      } catch (error) {
        console.error('Error polling test status:', error);
        pollingAttemptsRef.current += 1;

        if (pollingAttemptsRef.current >= maxPollingAttempts) {
          onError('Failed to check test status after multiple attempts');
          clearPolling();
          return;
        }

        // Schedule next status check
        const nextInterval = calculatePollingInterval(
          pollingAttemptsRef.current,
        );
        pollingIntervalRef.current = setTimeout(() => {
          pollTestRunStatus(testId, runId, callbacks);
        }, nextInterval);
      }
    },
    [clearPolling, maxPollingAttempts, calculatePollingInterval],
  );

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  return {
    pollTestRunStatus,
    clearPolling,
  };
};
